/**
 * 購入情報のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import { push } from 'connected-react-router';
import { AppState } from 'pages';
import {
  beginFetch,
  endFetch,
  failedFetch,
  FetchStatus
} from 'common/modules/app/fetch';
import {
  fetchFailure,
  cancelPurchaseError,
  ErrorResponse,
  clearErrors
} from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  getPurchaseHistoryRequest,
  deletePurchaseRequest,
  postIndividualCancelRequest
} from 'common/utils/request/sas-request';
import { GetPurchaseHistoryResponse } from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { getLogin } from 'common/modules/domain/user';
import { SasPagePath } from 'common/utils/urls';
import {
  SasCookieKeys,
  loadCookie,
  deleteCookie
} from 'common/utils/cookie-utils';
import { postAccessToken } from 'common/utils/token';
import { loggedOut } from 'common/modules/app/principal';
import { isDeletedPlan, isCancellationConfirmation } from '../app/purchase';
import { LOGIN_USER } from 'common/modules/domain/user';
import { InnerError } from '../app/innerErrorCode';
import {
  setStatus,
  BoardStatus,
  setIsIndividualCancel,
  setIsProcessing
} from '../app/board';

/** Actionとは別で共通関数を定義
 * 購入情報一覧の絞り込み関数
 * @param list: 絞り込みの元となるデータ
 * @param value: 絞り込みの条件になる値(urlのクエリパラメータ、プルダウンで選択された値。cookieからmodelNameの値を取得する際に取得できなかった場合は空文字を返すような処理になっているためvalueがnullになることはない)
 * @param typeValue: 絞り込みの条件になる値(urlのクエリパラメータ、type。cookieからtypeの値を取得する際に取得できなかった場合は空文字を返すような処理になっているためtypeValueがnullになることはない)
 */
export const filterList = (
  list: PurchaseState,
  value: string,
  typeValue: string
) => {
  let newState: PurchaseState = {};
  if (value && value === 'all') {
    // カメラ有償機能のみに絞り込む(deviceLimitTypeが2)
    newState = Object.fromEntries(
      Object.entries(list).filter(
        ([Key, purchase]) =>
          purchase.plan.deviceLimitType && purchase.plan.deviceLimitType === 2
      )
    );
  } else if (value !== '' && value !== 'all') {
    // pIdで絞り込み
    newState = Object.fromEntries(
      Object.entries(list).filter(
        ([key, purchase]) =>
          purchase.plan.devices &&
          purchase.plan.devices.some(
            (model) => model.toUpperCase() === value.toUpperCase()
          )
      )
    );
  } else if (typeValue && typeValue === 'app') {
    // カメラ有償機能以外のみに絞り込む(deviceLimitTypeがnull,1)
    newState = Object.fromEntries(
      Object.entries(list).filter(
        ([Key, purchase]) => purchase.plan.deviceLimitType !== 2
      )
    );
  } else if (typeValue !== '' && typeValue !== 'app') {
    // typeにapp以外の値が指定された場合該当プランなしとする
    newState = {};
  } else {
    // 全プラン
    newState = list;
  }
  return newState;
};

enum ActionTypes {
  RESET_STATE = 'domain/purchase/RESET_STATE',
  FETCH_SUCCESS = `domain/purchase/FETCH_SUCCESS`,
  DELETE_SUCCESS = `domain/purchase/DELETE_SUCCESS`,
  FILTER_STATE = 'domain/purchase/FILTER_STATE'
}

export enum StateType {
  // 契約中
  TYPE_CONTRACT = 'contract',
  // 解約済み
  TYPE_CANCEL = 'cancel',
  // デモ
  TYPE_DEMO = 'demo',
  // デモ解約済み
  TYPE_DEMO_CANCELED = 'demo_canceled',
  // 全て
  TYPE_ALL = 'all',
  // 一部解約済み
  TYPE_INDIVIDUAL_CANCEL = 'individual_cancel'
}

export enum SortType {
  // 購入開始日時でソート
  TYPE_PURCHASE_ID = 'purchaseId',
  // 購入日時・解約日時でソート
  TYPE_OPERATION_DATETIME = 'operationDatetime'
}

export const GET_PURCHASE_HISTORY_ID = 'get_purchase_history';
export const DELETE_PURCHASE_ID = 'delete_purchase';
export const POST_INDIVIDUAL_CANCEL = 'post_individual_cancel';
/**
 * 購入情報をクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * 購入情報を置き換える
 */
export const fetchSuccess = (
  list: PurchaseInformation[],
  modelName: string,
  type: string
) => {
  // PurchaseInformation[]をPurchaseStateに変換してActionを作成
  let purchaseState: PurchaseState = {};
  for (const data of list) {
    purchaseState = { ...purchaseState, [data.purchaseId]: data };
  }
  const customPayload = {
    purchaseState: purchaseState,
    modelName: modelName,
    type: type
  };
  return createAction(ActionTypes.FETCH_SUCCESS, customPayload);
};

/**
 * 購入情報を削除（解約）
 * @param purchaseId 購入ID
 */
export const deleteSuccess = (purchaseId: string) => {
  return createAction(ActionTypes.DELETE_SUCCESS, purchaseId);
};

/**
 * URLにクエリパラメータが指定されている場合購入情報を置き換える
 * initialPurchasePlanListは置き換えない
 */
export const filterState = (modelName: string, type: string) => {
  const customPayload = {
    modelName: modelName,
    type: type
  };
  return createAction(ActionTypes.FILTER_STATE, customPayload);
};

const actions = {
  resetState,
  fetchSuccess,
  deleteSuccess,
  filterState
};
type PurchaseAction = ActionsUnion<typeof actions>;

/**
 * 購入情報一覧を取得する
 * @param token: SASが発行した独自トークン
 * @param language: 言語コード
 * @param state: 状態(契約中：contract or 全て：all)
 * @param sort: ソート（購入開始日時：purchaseId or 購入日時・解約日時:operationDatetime）
 * @param from: 履歴取得開始日時(省略可)
 */
export const getPurchasesHistory = (
  token: string,
  language: string,
  state: string,
  sort: string,
  model: string | null,
  from?: string
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // cookieが保持しているmodelNameとtypeを取得
    const cookie = loadCookie();
    const modelName = cookie[SasCookieKeys.KEY_MODEL_NAME];
    const type = cookie[SasCookieKeys.KEY_TYPE];

    // キャンセルを判断する処理を定義
    const isCancel = () => {
      const status = getState().app.fetch[GET_PURCHASE_HISTORY_ID];
      return status === FetchStatus.CANCEL;
    };
    // GET_PURCHASE_HISTORY_IDを処理中に変更
    dispatch(beginFetch(GET_PURCHASE_HISTORY_ID));

    // ErrorStateが購入情報取得だった場合はリセットする
    if (getState().app.error.requestId === GET_PURCHASE_HISTORY_ID) {
      dispatch(clearErrors());
    }

    let nextKey = '';
    from = from ? from : '';
    do {
      // プラン購入済み情報一覧取得APIを実行
      const requestData = getPurchaseHistoryRequest(
        token,
        language,
        state,
        sort,
        from,
        nextKey
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_PURCHASE_HISTORY_ID;
        if (
          error.statusCode &&
          (error.statusCode === 401 || error.statusCode === 403)
        ) {
          // 認証エラーの場合は再ログイン
          await dispatch(getLogin());
          // 再ログインに失敗した場合はエラー
          const status = getState().app.fetch[LOGIN_USER];
          if (status === FetchStatus.FAILED) {
            const innerError = InnerError.loginFailed;
            innerError.requestId = GET_PURCHASE_HISTORY_ID;
            dispatch(fetchFailure(innerError));
            dispatch(failedFetch(GET_PURCHASE_HISTORY_ID));
          }
          return;
        } else {
          dispatch(fetchFailure(error));
          dispatch(failedFetch(GET_PURCHASE_HISTORY_ID));
          return;
        }
      }
      // APIが成功した場合は取得したプラン購入済み情報でstateを更新
      const body: GetPurchaseHistoryResponse = (response.body as unknown) as GetPurchaseHistoryResponse;
      // 取得したプラン購入済み情報でstateを更新
      await dispatch(fetchSuccess(body.purchases, modelName, type));

      // nextKeyの有無、キャンセルの有無を判断
      if (body.nextKey === '' || isCancel()) {
        // キャンセル又は次の取得が無いため処理を抜ける
        nextKey = '';
      } else {
        // nextKeyを更新
        nextKey = body.nextKey;
      }
    } while (nextKey);
    // GET_PURCHASE_HISTORY_IDを未処理に変更
    dispatch(endFetch(GET_PURCHASE_HISTORY_ID));
  };
};

/**
 * 購入情報を削除する（解約する）
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param vendorId: 支払いベンダーID
 * @param purchaseId: 購入ID
 * @param language: 言語
 * @param isRetry: 再実施か否か
 */
export const deletePurchase = (
  token: string,
  refreshToken: string,
  vendorId: string,
  purchaseId: string,
  language: string,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // DELETE_PURCHASE_IDを処理中に変更
    dispatch(beginFetch(DELETE_PURCHASE_ID));
    // 解約APIを実行
    const requestData = deletePurchaseRequest(token, vendorId, purchaseId);
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      true
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = DELETE_PURCHASE_ID;
      // 403エラーの場合はトークン更新後に解約を再実施
      // 但し、再実施で起きた場合は対象外（＝解約手続きエラー画面）とする
      let needLogout = false;
      if (error.statusCode && error.statusCode === 403) {
        if (isRetry === undefined || !isRetry) {
          // トークン更新
          await dispatch(postAccessToken(token, refreshToken));
          // トークン更新に成功した場合は、解約を再実施
          const cookie = loadCookie();
          if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
            await dispatch(
              deletePurchase(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                vendorId,
                purchaseId,
                language,
                true
              )
            );
            // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
            return;
          } else {
            // トークン更新に失敗の場合はログアウト状態にさせる
            needLogout = true;
          }
        } else {
          // 再実施でも403だった時はログアウト状態にさせる
          needLogout = true;
        }
      }
      if ((error.statusCode && error.statusCode === 401) || needLogout) {
        // 最終的に401または403であった場合は、SASのみログアウト状態にする
        deleteCookie(SasCookieKeys.KEY_TOKEN);
        deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
        dispatch(loggedOut());
      }
      // 403エラーでトークン更新に成功した以外は購入手続きエラー画面へ遷移
      dispatch(cancelPurchaseError(error));
      dispatch(push(SasPagePath.Error));
      dispatch(failedFetch(DELETE_PURCHASE_ID));
      return;
    } else {
      // APIが成功した場合は、購入履歴情報を取り直す
      await dispatch(reset());
      await dispatch(
        getPurchasesHistory(
          token,
          language,
          StateType.TYPE_ALL,
          SortType.TYPE_PURCHASE_ID,
          null
        )
      );
    }
    // 解約完了画面へ遷移
    dispatch(isDeletedPlan(true));
    dispatch(isCancellationConfirmation(false));
    dispatch(push(SasPagePath.DeleteCompletion));

    // DELETE_PURCHASE_IDを未処理に変更
    dispatch(endFetch(DELETE_PURCHASE_ID));
  };
};

/**
 * 購入情報の一部解約
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param vendorId: 支払いベンダーID
 * @param purchaseId: 購入ID
 * @param language: 言語
 * @param isRetry: 再実施か否か
 * @param indexes: 解約するインデックス番号のリスト
 */
export const postIndividualCancel = (
  token: string,
  refreshToken: string,
  vendorId: string,
  purchaseId: string,
  language: string,
  indexes: string[],
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // DELETE_PURCHASE_IDを処理中に変更
    dispatch(beginFetch(POST_INDIVIDUAL_CANCEL));
    dispatch(setIsProcessing(true));
    dispatch(setIsIndividualCancel(true));
    // 個別解約APIを実行
    const requestData = postIndividualCancelRequest(
      token,
      vendorId,
      purchaseId,
      indexes
    );
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      true
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = POST_INDIVIDUAL_CANCEL;
      // 403エラーの場合はトークン更新後に解約を再実施
      // 但し、再実施で起きた場合は対象外（＝解約手続きエラー画面）とする
      let needLogout = false;
      if (error.statusCode && error.statusCode === 403) {
        if (isRetry === undefined || !isRetry) {
          // トークン更新
          await dispatch(postAccessToken(token, refreshToken));
          // トークン更新に成功した場合は、個別解約を再実施
          const cookie = loadCookie();
          if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
            await dispatch(
              postIndividualCancel(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                vendorId,
                purchaseId,
                language,
                indexes,
                true
              )
            );
            // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
            return;
          } else {
            // トークン更新に失敗の場合はログアウト状態にさせる
            needLogout = true;
          }
        } else {
          // 再実施でも403だった時はログアウト状態にさせる
          needLogout = true;
        }
      }
      if ((error.statusCode && error.statusCode === 401) || needLogout) {
        // 最終的に401または403であった場合は、SASのみログアウト状態にする
        deleteCookie(SasCookieKeys.KEY_TOKEN);
        deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
        dispatch(loggedOut());
      }
      // 403エラーでトークン更新に成功した以外は個別解約失敗のポップアップ表示
      dispatch(failedFetch(POST_INDIVIDUAL_CANCEL));
      dispatch(setIsProcessing(false));
      // 個別解約操作完了ボード表示
      dispatch(setStatus(BoardStatus.COMPLETION_BEFORE_INDIVIDUAL_CANCEL_DISP));
      return;
    } else {
      // APIが成功した場合は、購入履歴情報を取り直す
      await dispatch(reset());
      await dispatch(
        getPurchasesHistory(
          token,
          language,
          StateType.TYPE_ALL,
          SortType.TYPE_PURCHASE_ID,
          null
        )
      );
    }
    // DELETE_PURCHASE_IDを未処理に変更
    dispatch(endFetch(POST_INDIVIDUAL_CANCEL));
    // 個別解約完了のポップアップ表示用にステータスを変更
    dispatch(setIsProcessing(false));
  };
};

/**
 * 購入情報をクリアする
 */
export const reset = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * 購入情報に指定されたプランIDが契約中か確認する
 */
export const checkContractId = (state: PurchaseState, id: string) => {
  if (id in state) {
    // 存在する場合は契約中かどうか確認する
    if (StateType.TYPE_CONTRACT == state[id].state) {
      return true;
    }
  }
  return false;
};

/**
 * Stateから、契約中プラン、デモプラン、解約済みだが有効期限(次回更新日時+猶予日時)内のプラン、を返す
 *  state: PurchaseState
 *  ascendingOrder: ソート順(true=昇順、false=降順)
 */
export const getContractedPlan = (
  state: PurchaseState,
  ascendingOrder: boolean
) => {
  // レスポンス用の購入情報
  const datas: PurchaseInformation[] = [];
  // stateの中身をループでチェック
  for (const [key, value] of Object.entries(state)) {
    // 契約中の場合
    if (
      StateType.TYPE_CONTRACT == value.state ||
      StateType.TYPE_DEMO == value.state ||
      StateType.TYPE_INDIVIDUAL_CANCEL == value.state
    ) {
      if (value.lendingStatus) {
        // 貸与されているプランの場合、有効期限を確認する
        if (!value.expirationDatetime) {
          // 有効期限がないプランの場合、契約中一覧に載せる
          datas.push(value);
        } else {
          // MEMO: l.480～493に同処理があるため、メンテする場合は両方に手を加える
          // 有効期限があるプランの場合、次回更新日時+猶予日時の値を計算する
          const grace_days = value.expirationGraceDays
            ? value.expirationGraceDays
            : 0;
          const expirationDate = new Date(value.expirationDatetime);
          const limitDate = new Date(
            expirationDate.setDate(expirationDate.getDate() + grace_days)
          );
          // 次回更新日時+猶予日時なら契約中一覧に載せる
          if (Date.now() <= limitDate.getTime()) {
            datas.push(value);
          }
        }
      } else {
        // 購入したプランの場合、契約中一覧に載せる
        datas.push(value);
      }
    } else if (StateType.TYPE_CANCEL == value.state) {
      // サブスクプランの場合
      if (value.expirationDatetime) {
        // 次回更新日時+猶予日時の値を計算する
        const grace_days = value.expirationGraceDays
          ? value.expirationGraceDays
          : 0;
        const expirationDate = new Date(value.expirationDatetime);
        const limitDate = new Date(
          expirationDate.setDate(expirationDate.getDate() + grace_days)
        );
        // 解約済みでも次回更新日時+猶予日時なら契約中一覧に載せる
        if (Date.now() <= limitDate.getTime()) {
          datas.push(value);
        }
      }
    }
  }
  return sortPurchaseId(datas, ascendingOrder);
};

/**
 * Stateから6か月以内の購入履歴のみ返す。デモプランの履歴は含めない。
 *  state: PurchaseState
 *  ascendingOrder: ソート順(true=昇順、false=降順)
 */
export const getPurchaseHistoryWithin6Months = (
  state: PurchaseState,
  ascendingOrder: boolean
) => {
  // レスポンス用の購入情報
  const datas: PurchaseInformation[] = [];
  // 現在時刻の値(世界協定時からの経過時間をミリ秒単位)を取得
  const now = Date.now();
  // 6か月(180日)の値(ミリ秒単位)を定義
  // 180日 => 24h × 180 => 60 × 24 × 180 => 60 × 60 × 24 × 180 => 1000 × 60 × 60 × 24 × 180
  const display_period = 15552000000;
  // stateの中身をループでチェック
  for (const [key, value] of Object.entries(state)) {
    // デモプランの履歴,貸与履歴は対象外とする
    if (
      StateType.TYPE_DEMO_CANCELED == value.state ||
      StateType.TYPE_DEMO == value.state ||
      value.lendingStatus
    ) {
      continue;
    }
    // ユーザー購入または解約日時を数値(世界協定時からの経過時間をミリ秒単位)に変換
    const check_time = Date.parse(value.operationDatetime);
    // 「現在時刻 <= 履歴の時間 + 180日の値」の場合、180日以内のデータのため対象とする
    if (now <= check_time + display_period) {
      datas.push(value);
    }
  }
  return sortOperationDatetime(datas, ascendingOrder);
};

/**
 * PurchaseInformationのリストをoperationDatetimeでソートしてリストで返す
 *  datas: ソート対象のPurchaseInformationのリスト
 *  ascendingOrder: ソート順(true=昇順、false=降順)
 */
const sortOperationDatetime = (
  datas: PurchaseInformation[],
  ascendingOrder: boolean
) => {
  if (ascendingOrder) {
    datas.sort(function (a, b) {
      return Date.parse(a.operationDatetime) - Date.parse(b.operationDatetime);
    });
  } else {
    datas.sort(function (a, b) {
      return Date.parse(b.operationDatetime) - Date.parse(a.operationDatetime);
    });
  }
  return datas;
};

/**
 * PurchaseInformationのリストをpurchaseIdでソートしてリストで返す
 *  datas: ソート対象のPurchaseInformationのリスト
 *  ascendingOrder: ソート順(true=昇順、false=降順)
 */
const sortPurchaseId = (
  datas: PurchaseInformation[],
  ascendingOrder: boolean
) => {
  if (ascendingOrder) {
    datas.sort(function (a, b) {
      return a.purchaseId < b.purchaseId ? -1 : 1;
    });
  } else {
    datas.sort(function (a, b) {
      return a.purchaseId > b.purchaseId ? -1 : 1;
    });
  }
  return datas;
};

/**
 * 購入情報
 */
export interface PurchaseInformation {
  /** ユーザー購入番号(SAS発行) */
  purchaseId: string;
  /** ユーザー購入番号(決済ベンダー発行の注文ID) */
  vendorPurchaseId: string;
  /** 購入プラン */
  plan: PurchasePlanInformation;
  /** 購入サービス */
  services: PurchaseServiceInformation[];
  /** 状態("contract"：契約中 or "cancel"：解約 or "demo"：デモ or "demo_canceled": デモ解約 or "individual_cancel" : 個別解約) */
  state: StateType;
  /** ユーザー購入または解約日時 (ISO8601 UTC) */
  operationDatetime: string;
  /** サブスクリプション次回更新日時、利用量制限のある売り切りプランの有効期限(ISO8601 UTC)。売り切りの場合は空 */
  expirationDatetime: string | null;
  /** サブスクリプションまたは利用量制限のある売り切りプランの猶予日数 */
  expirationGraceDays: number | null;
  /** サブスクリプション最終更新日時(ISO8601 UTC)。売り切りの場合は空 */
  updateDatetime: string | null;
  /** 購入数量  */
  quantity: number | null;
  /** デバイス制限上限 */
  planLimit: number | null;
  /** デバイス利用量 */
  planUsage: number | null;
  /** 貸与状態 */
  lendingStatus: string;
  /** 貸与数 */
  lendingQuantity: number | null;
  /** サブスクリプション更新種別(自動："Automatic", 手動："Manual") */
  renewalType: string | null;
}

export interface PurchasePlanInformation {
  /** 購入プランID */
  planId: string;
  /** プラン名 */
  name: string;
  /** 説明 */
  description: string;
  /** 画像URL */
  imageUrl: string;
  /** 価格情報 */
  price: pricesInformation;
  /** 税コード */
  taxCode: string;
  /** サブスクリプション有効日数(売り切りの場合はnull) */
  validityPeriod: number | null;
  /** 利用量制限プラン有効期間(利用量制限の売り切りプラン以外はnull) */
  perpetualValidityPeriod: number | null;
  /** デバイス制限種別(1が設定されている場合、第三者貸与が可能。2の場合はカメラ有償機能プラン) */
  deviceLimitType: number | null;
  /** 第三者利用登録種別(1が設定されている場合、第三者利用登録が可能。) */
  lendingType: number | null;
  /** サポートデバイスの機種IDのリスト(deviceLimitTypeが2以外の時は空配列) */
  devices: string[];
}

export interface PurchaseServiceInformation {
  /** サービスID */
  serviceId: string;
  /** サービス名 */
  name: string;
  /** 利用量上限(利用量制限プラン以外はnull) */
  limit: number | null;
  /** 利用量(利用量制限プラン以外はnull) */
  usage: number | null;
}

/**
 * 価格情報
 */
interface pricesInformation {
  /** 国 */
  country: string;
  /** 価格 */
  price: number;
  /** 通貨 */
  currency: string;
  /** 決済ベンダー */
  payment: string;
  /** 決済ベンダー商品ID */
  paymentProductId: string;
  /** 税込み価格 */
  taxIncludedPrice: number;
}

/**
 * 購入情報のState
 * キーにユーザー購入番号(SAS発行)、値に購入情報を持つ
 */
export interface PurchaseState {
  [key: string]: PurchaseInformation;
}

export interface PurchaseInitialState {
  purchasePlanList: PurchaseState;
  initialPurchasePlanList: PurchaseState;
}

const initialState: PurchaseInitialState = {
  purchasePlanList: {},
  initialPurchasePlanList: {}
};

export default (
  state: PurchaseInitialState = initialState,
  actions: PurchaseAction
): PurchaseInitialState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      const newState: PurchaseState = {};
      Object.keys(state.initialPurchasePlanList).forEach((key) => {
        if (key in actions.payload.purchaseState) {
          newState[key] = actions.payload.purchaseState[key];
        } else {
          newState[key] = state.initialPurchasePlanList[key];
        }
      });
      Object.keys(actions.payload.purchaseState).forEach((key) => {
        if (!(key in newState)) {
          newState[key] = actions.payload.purchaseState[key];
        }
      });

      // 取得したデータ＋既存データに対して絞り込みを実施する
      const filteredPurchasePlnList = filterList(
        newState,
        actions.payload.modelName,
        actions.payload.type
      );

      // 画面に表示するpurchasePlanListは絞り込み後のデータで、初期値を保持したいinitialPurchasePlanListはapiのレスポンスのデータで更新する
      return {
        purchasePlanList: filteredPurchasePlnList,
        initialPurchasePlanList: newState
      };
    }
    case ActionTypes.RESET_STATE: {
      return initialState;
    }
    case ActionTypes.DELETE_SUCCESS: {
      const modifiedState = { ...state.purchasePlanList };
      delete modifiedState[actions.payload];
      return { ...state, purchasePlanList: modifiedState };
    }
    case ActionTypes.FILTER_STATE: {
      const newState = filterList(
        state.initialPurchasePlanList,
        actions.payload.modelName,
        actions.payload.type
      );
      return { ...state, purchasePlanList: newState };
    }
    default: {
      return state;
    }
  }
};
